import React, { useContext } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import PropTypes from "prop-types";

import AnalyticsContext from "./analyticsContext";
import colors from "../constants/graphColors";

ChartJS.register(CategoryScale, LinearScale, LogarithmicScale, BarElement, Title, Tooltip, Legend);

function BarChart({ className, chartData, title, displayCurrencySymbol }) {
  const { isStacked, params } = useContext(AnalyticsContext);
  const { columns, content } = chartData;

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          color: "black",
          padding: 20,
          pointStyle: "rectRounded",
        },
      },
      title: {
        display: true,
        text: title,
        font: {
          size: 18,
        },
        color: "black",
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem) =>
            `${tooltipItem.dataset.label}: ${displayCurrencySymbol && "$"}${tooltipItem.formattedValue}`,
        },
      },
    },
    scales: {
      x: {
        stacked: !!isStacked,
        ticks: {
          color: "black",
          font: {
            size: 10,
          },
        },
      },
      y: {
        stacked: !!isStacked,
        ticks: {
          color: "black",
          font: {
            size: 10,
          },
          ...(displayCurrencySymbol && {
            callback(value) {
              return new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
                trailingZeroDisplay: "stripIfInteger",
              }).format(value);
            },
          }),
        },
      },
    },
  };

  let labels = columns.slice(1).map((column) => column);

  labels = labels.map((label) => {
    if (!label.includes(",")) return label;
    const [year, month] = label.split(",");
    return `${month}, ${year}`;
  });
  // Row results for the year are reversed for some reason, required extra formatting.
  const createFreightDataset = () =>
    content.map((row, i) => {
      let label;
      let data;
      if (params.analytics_filter_by === "year") {
        label = row[Object.keys(row)[Object.keys(row).length - 1]];
        data = Object.values(row)
          .slice(0, -1)
          .map((value) => parseFloat(value));
      } else {
        label = row[Object.keys(row)[0]];
        data = Object.values(row)
          .slice(1)
          .map((value) => parseFloat(value));
      }
      const backgroundColor = colors[i];
      const borderRadius = 4;
      const maxBarThickness = 100;
      return { label, data, backgroundColor, borderRadius, maxBarThickness };
    });

  const data = {
    labels,
    datasets: createFreightDataset(),
  };

  return (
    <div style={{ maxWidth: "1600px" }}>
      <Bar className={className} options={options} data={data} />
    </div>
  );
}

BarChart.propTypes = {
  className: PropTypes.string,
  chartData: PropTypes.instanceOf(Object).isRequired,
  title: PropTypes.string.isRequired,
  displayCurrencySymbol: PropTypes.bool,
};

BarChart.defaultProps = {
  className: "",
  displayCurrencySymbol: false,
};

export default BarChart;
